import { ApiResponseType } from '@/types/sharedTypes';
import { JSONError } from '@/bundle/Error/classes/JSONError';
import { ApiParams, urlApiHelper } from './helpers/apiHelpers';
import {
  ACCESS_DENIED_RESPONSE,
  APPLICATION_JSON_CONTENT_TYPE,
  BLOCKED_USER_RESPONSE,
  DEFAULT_LANGUAGE,
  HTTP_FORBIDDEN_STATUS,
} from './const';
import { LOGIN_URL } from './v1/users/public/loginApi';
import { AccessDeniedError } from '@/bundle/Error/classes/AccessDeniedError';
import { BlockedUserError } from '@/bundle/Error/classes/BlockedUserError';

const getContentType = (headers: Headers) => {
  return headers.get('content-type');
};

const buildOptions = async (headers: HeadersInit = {}) => {
  const options: RequestInit = {
    headers: {
      'Content-Type': APPLICATION_JSON_CONTENT_TYPE,
      'Accept-Language': DEFAULT_LANGUAGE,
      ...headers,
    },
  };

  return options;
};

export const apiService = {
  async GET<T>(uri: string, params?: ApiParams): Promise<ApiResponseType<T>> {
    const url = urlApiHelper(uri, params);
    const options = await buildOptions();
    const response = await fetch(url, options);

    return apiService.handleResponse(response);
  },

  async POST<T>(uri: string, data: unknown, params?: ApiParams): Promise<ApiResponseType<T>> {
    const url = urlApiHelper(uri, params);
    const options = await buildOptions();
    // Apply credentials to login URI, so we can apply auth cookie
    const shouldIncludeCredentials = uri === LOGIN_URL;
    const response = await fetch(url, {
      method: 'POST',
      headers: options.headers,
      body: JSON.stringify(data),
      ...(shouldIncludeCredentials && { credentials: 'include' }),
    });

    return apiService.handleResponse(response);
  },

  async PATCH<T>(uri: string, data: unknown, params?: ApiParams): Promise<ApiResponseType<T>> {
    const url = urlApiHelper(uri, params);
    const options = await buildOptions();
    const response = await fetch(url, {
      method: 'PATCH',
      headers: options.headers,
      body: JSON.stringify(data),
    });

    return apiService.handleResponse(response);
  },

  async handleResponse(response: Response) {
    const contentType = getContentType(response.headers);
    const { status } = response;

    if (contentType !== APPLICATION_JSON_CONTENT_TYPE) {
      throw new JSONError();
    }

    const json = await response.json();

    if (status === HTTP_FORBIDDEN_STATUS && json === ACCESS_DENIED_RESPONSE) {
      throw new AccessDeniedError();
    }

    if (status === HTTP_FORBIDDEN_STATUS && json === BLOCKED_USER_RESPONSE) {
      throw new BlockedUserError();
    }

    return { response, json };
  },
};
